import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { api, signOut } from "./lib/api";
import { supabase } from "./lib/supabase";
import { C, applyTheme, currentTheme } from "./lib/theme";
import Login from "./pages/Login";
import Home from "./pages/Home";
import Board from "./pages/Board";
import Messages from "./pages/Messages";
import Calendar from "./pages/Calendar";
import Assessments from "./pages/Assessments";
import Profile from "./pages/Profile";
import Moderation from "./pages/Moderation";
import PublicProfile from "./pages/PublicProfile";
import Calculator from "./pages/Calculator";
import Intake from "./pages/Intake";
import IntakeLinks from "./pages/IntakeLinks";
import ClientPrep from "./pages/ClientPrep";
import Onboarding from "./pages/Onboarding";
import Articles from "./pages/Articles";
import Mission from "./pages/Mission";

export default function App() {
  const [me, setMe] = useState(null);
  const [theme, setTheme] = useState(currentTheme());
  const switchTheme = (k) => { applyTheme(k); setTheme(k); };
  const [authed, setAuthed] = useState(false);
  useEffect(() => {
    const DEV = import.meta.env.VITE_AUTH_DEV === "true";
    const hasDev = () => DEV && !!localStorage.getItem("ac_dev_token");
    supabase.auth.getSession().then(({ data }) => setAuthed(!!data.session || hasDev()));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setAuthed(!!s || hasDev()));
    return () => sub.subscription.unsubscribe();
  }, []);
  useEffect(() => {
    if (authed) api.me().then(setMe).catch(() => setAuthed(false));
    else setMe(null);
  }, [authed]);

  if (!authed) return (
    <BrowserRouter>
      <Routes>
        <Route path="/calculator" element={<Calculator />} />
        <Route path="/intake/:token" element={<Intake />} />
        <Route path="/articles" element={<Articles />} />
        <Route path="/articles/:id" element={<Articles />} />
        <Route path="/mission" element={<Mission />} />
        <Route path="*" element={<Login onLogin={() => setAuthed(true)} />} />
      </Routes>
    </BrowserRouter>
  );

  if (authed && me && !me.onboarded_at)
    return <Onboarding me={me} onDone={() => api.me().then(setMe)} />;

  return (
    <BrowserRouter>
      <div style={{ minHeight: "100vh", background: C.bg, color: C.text,
        fontFamily: C.font }}>
        <Header me={me} />
        <main style={{ maxWidth: 1100, margin: "0 auto", padding: "18px 22px 60px" }}>
          <Routes>
            <Route path="/" element={<Home me={me} />} />
            <Route path="/calendar" element={<Calendar me={me} />} />
            <Route path="/board" element={<Board me={me} />} />
            <Route path="/board/:id" element={<Board me={me} />} />
            <Route path="/messages" element={<Messages me={me} />} />
            <Route path="/assessments" element={<Assessments me={me} />} />
            <Route path="/profile" element={<Profile me={me} refresh={() => api.me().then(setMe)}
              theme={theme} onTheme={switchTheme} />} />
            <Route path="/u/:name" element={<PublicProfile />} />
            <Route path="/articles" element={<Articles me={me} />} />
            <Route path="/articles/:id" element={<Articles me={me} />} />
            <Route path="/mission" element={<Mission me={me} />} />
            <Route path="/calculator" element={<Calculator />} />
            <Route path="/intake/:token" element={<Intake />} />
            <Route path="/intake-links" element={me?.plan === "professional" ? <IntakeLinks me={me} /> : <Navigate to="/" />} />
            <Route path="/clients/:chartId" element={me?.plan === "professional" ? <ClientPrep /> : <Navigate to="/" />} />
            <Route path="/clients/:chartId/print" element={me?.plan === "professional" ? <ClientPrep print /> : <Navigate to="/" />} />
            <Route path="/moderation" element={me?.is_moderator ? <Moderation /> : <Navigate to="/" />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}

function Header({ me }) {
  const loc = useLocation();
  const [isMobile, setIsMobile] = useState(() =>
    window.matchMedia("(max-width: 760px)").matches);
  const [toolsOpen, setToolsOpen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 760px)");
    const on = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  useEffect(() => setToolsOpen(false), [loc.pathname]);

  const tab = ({ isActive }) => ({
    color: isActive ? C.text : C.dim, textDecoration: "none", padding: "8px 14px",
    borderBottom: isActive ? `2px solid ${C.brass}` : "2px solid transparent",
    fontSize: 13.5,
  });
  const tools = [
    ["/calendar", "My Calendar"],
    ["/messages", "Messages"],
    ["/assessments", "Assessments"],
    ["/articles", "Articles"],
    ["/mission", "Mission"],
    ["/profile", "Profile"],
    ...(me?.plan === "professional" ? [["/intake-links", "Client Intake"]] : []),
    ...(me?.is_moderator ? [["/moderation", "Moderation"]] : []),
  ];
  const inTools = tools.some(([p]) => loc.pathname.startsWith(p));

  return (
    <header style={{ display: "flex", alignItems: "baseline", gap: isMobile ? 10 : 18,
      flexWrap: "wrap", padding: "18px 22px 0", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ fontFamily: C.displayFont, fontSize: isMobile ? 21 : 28 }}>
        Astrology Calendar
      </div>
      <nav style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline" }}>
        <NavLink to="/" style={tab} end>Home</NavLink>
        <NavLink to="/board" style={tab}>Message Board</NavLink>
        {!isMobile && <>
          <NavLink to="/calendar" style={tab}>My Calendar</NavLink>
          <NavLink to="/messages" style={tab}>Messages</NavLink>
          <NavLink to="/assessments" style={tab}>Assessments</NavLink>
          <NavLink to="/articles" style={tab}>Articles</NavLink>
          <NavLink to="/mission" style={tab}>Mission</NavLink>
          <NavLink to="/profile" style={tab}>Profile</NavLink>
          {me?.plan === "professional" && <NavLink to="/intake-links" style={tab}>Client Intake</NavLink>}
          {me?.is_moderator && <NavLink to="/moderation" style={tab}>Moderation</NavLink>}
        </>}
        {isMobile && (
          <span style={{ position: "relative" }}>
            <button onClick={() => setToolsOpen((o) => !o)}
              style={{ all: "unset", cursor: "pointer", padding: "8px 14px", fontSize: 13.5,
                color: inTools || toolsOpen ? C.text : C.dim,
                borderBottom: inTools ? `2px solid ${C.brass}` : "2px solid transparent" }}>
              Tools {toolsOpen ? "▴" : "▾"}
            </button>
            {toolsOpen && (
              <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 60,
                background: C.surface, border: `1px solid ${C.line}`, borderRadius: 8,
                padding: 6, minWidth: 160, boxShadow: "0 8px 24px rgba(0,0,0,0.35)" }}>
                {tools.map(([to, lbl]) => (
                  <NavLink key={to} to={to} onClick={() => setToolsOpen(false)}
                    style={({ isActive }) => ({ display: "block", padding: "9px 12px",
                      borderRadius: 5, textDecoration: "none", fontSize: 13.5,
                      color: isActive ? C.brass : C.text })}>{lbl}</NavLink>
                ))}
              </div>
            )}
          </span>
        )}
      </nav>
      <span style={{ marginLeft: "auto", fontSize: 11.5, color: C.faint }}>
        {me ? <>{me.display_name || "unnamed"} · {me.title}
          {me.plan === "professional" && <span style={{ color: C.brass }}> ✶</span>}
          {" · "}<button onClick={() => signOut().then(() => location.reload())}
            style={{ all: "unset", cursor: "pointer", color: C.brass }}>sign out</button>
        </> : "…"}
      </span>
    </header>
  );
}
